import { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getCategoryName } from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);

  const relatedArticles = useMemo(
    () =>
      article
        ? articles
            .filter((a) => a.category === article.category && a.id !== article.id)
            .slice(0, 3)
        : [],
    [article]
  );

  const breadcrumbItems = useMemo(
    () =>
      article
        ? [
            { label: '首页', href: '/' },
            { label: getCategoryName(article.category), href: `/category/${article.category}` },
            { label: article.title },
          ]
        : [],
    [article]
  );

  const articleSchema = useMemo(
    () =>
      article
        ? {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            author: {
              '@type': 'Organization',
              name: '穿搭换装指南',
            },
            publisher: {
              '@type': 'Organization',
              name: '穿搭换装指南',
            },
            image: article.image,
            url: `https://yourdomain.com/article/${article.id}`,
            inLanguage: 'zh-CN',
          }
        : null,
    [article]
  );

  if (!article) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <ScrollToTop />
      {articleSchema && <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>}

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="absolute inset-0 w-full h-full object-cover object-top"
              title={article.title}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
            <div className="relative z-10 w-full h-full flex flex-col items-center justify-end px-4 pb-8 md:pb-10">
              <div className="max-w-3xl text-center">
                <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-500 text-background-50 mb-4">
                  {getCategoryName(article.category)}
                </span>
                <h1 className="font-heading text-lg md:text-2xl lg:text-3xl text-background-50 leading-snug">
                  {article.title}
                </h1>
              </div>
            </div>
          </section>

          <Breadcrumb items={breadcrumbItems} />

          <article className="w-full px-4 md:px-6 py-6 md:py-10">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-4 text-sm text-foreground-400 mb-8 pb-6 border-b border-background-200/70">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime} 分钟阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  12.8k 阅读
                </span>
              </div>

              <div
                className="prose-custom"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-foreground-500">标签：</span>
                  <span className="px-3 py-1 text-xs bg-secondary-100 text-secondary-800 rounded-full whitespace-nowrap">
                    {getCategoryName(article.category)}
                  </span>
                  <span className="px-3 py-1 text-xs bg-secondary-100 text-secondary-800 rounded-full whitespace-nowrap">
                    穿搭指南
                  </span>
                  <span className="px-3 py-1 text-xs bg-secondary-100 text-secondary-800 rounded-full whitespace-nowrap">
                    优雅换装
                  </span>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
              <div className="max-w-7xl mx-auto">
                <h2 className="font-heading text-lg md:text-2xl text-foreground-900 mb-6">
                  相关文章推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} {...ra} />
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="w-full px-4 md:px-6 py-6">
            <div className="max-w-3xl mx-auto flex flex-col sm:flex-row gap-3">
              <Link
                to={`/category/${article.category}`}
                className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> 返回{getCategoryName(article.category)}分类
              </Link>
              <Link
                to="/"
                className="flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-home-line"></i> 回到首页
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}